/* eslint-disable */
import {
    PromotionQuestionsState,
    CsvReadFile,
    CsvReadItem,
} from 'src/AppState/PromotionQuestionsState/PromotionQuestionsState';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import React from 'react';
import { selectFile } from 'src/utils/selectFile';
import * as Papa from 'papaparse';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel as FormModelNew } from 'src_common/common/mobx-utils/Form2/FormModel';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';

type TestType = {
    question: string;
};

interface FormToSendType {
    question: string;
    file: boolean;
    status: boolean;
}

const _7_MB = 7340032;

export class QuestionFormStateV2 {
    public readonly maxQuestionLength: number = 90;
    public readonly maxFileSize: number = _7_MB;
    public readonly allowedMimeTypes: string[] = ['text/csv'];
    public readonly allowedFileTypes: string[] = ['.csv'];

    @observable public isLoading: boolean = false;
    @observable public errorMessage: string = '';

    @observable public questionFileName: string | null = null;
    @observable public questionFile: CsvReadFile | null = null;

    public readonly questionData: CsvReadItem | null;

    public readonly questionV2: FormInputState<string, string>;
    public readonly questionModelV2: FormModelNew<TestType>;
    public readonly fileV2: FormInputState<boolean, boolean>;
    public readonly formV2: FormModelNew<FormToSendType>;

    public constructor(private readonly promotionQuestionsState: PromotionQuestionsState) {
        this.promotionQuestionsState = promotionQuestionsState;
        this.questionData = promotionQuestionsState.currentQuestion;
        this.questionV2 = FormInputState.new(this.questionData?.name || '').map(
            validateNotEmpty('This field cant be empty')
        );
        this.questionModelV2 = FormModelNew.group({
            question: this.questionV2,
        });

        if (this.questionData) {
            const { fileName, uploadedAt, possibleAnswers } = this.questionData;
            if (fileName && uploadedAt && possibleAnswers) {
                const file = new CsvReadFile(fileName, uploadedAt, possibleAnswers);
                this.questionFile = file;
            } else {
                this.questionFile = null;
            }
        } else {
            this.questionFile = null;
        }

        this.fileV2 = FormInputState.new(Boolean(this.questionFile));

        this.formV2 = FormModelNew.group({
            question: this.questionV2,
            file: this.fileV2,
            status: FormInputState.new(false),
        });
        makeAutoObservable(this);
    }

    @computed get isFormValid(): boolean {
        return this.fileV2.value === true && this.formV2.result.value.type === 'ok';
    }

    @action handleSelectFile = async (files: File[]): Promise<void> => {
        const file = files[0];
        //@ts-expect-error
        this.questionFileName = file.name;
        try {
            //@ts-expect-error
            const parsedFile = await this.parseCSV(file);
            if (parsedFile.length > 0) {
                this.fileV2.setValue(true);
                //@ts-expect-error
                this.questionFile = new CsvReadFile(file.name, Date.now().toString(), parsedFile.flat());
            } else {
                throw new Error('File is corrupted, unreadable or empty.');
            }
        } catch (error) {
            console.log(error);
        }
        this.questionFileName = null;
    };

    private parseCSV = async (file: File): Promise<string[]> => {
        return new Promise((resolve, reject) => {
            Papa.parse<string>(file, {
                error: (error) => {
                    reject(error.message);
                },
                complete: (result) => {
                    resolve(result.data);
                },
            });
        });
    };

    @action handleRemoveFile = (): void => {
        this.fileV2.setValue(false);
        this.questionFileName = null;
        this.questionFile = null;
    };

    @action handleReplaceFile = async (event: React.SyntheticEvent): Promise<void> => {
        this.preventDefaults(event);
        const filesList = await selectFile({ acceptFileType: this.allowedFileTypes.join(',') });
        const files = Array.from(filesList);
        if (files.length > 0) {
            this.handleRemoveFile();
            await this.handleSelectFile(files);
        }
    };

    @action handleCancel = () => {
        this.promotionQuestionsState.setCurrentQuestion(null);
        this.questionV2.setValue('');
        this.formV2.reset();
    };

    @action handleSubmit = async () => {
        this.formV2.setAsVisited();
        const { result } = this.questionModelV2;
        this.isLoading = true;

        if (result.value.type === 'ok') {
            const payload: Partial<CsvReadItem> = {
                id: this.questionData?.id,
                name: result.value.data.question,
                active: false,
                ...this.questionFile, // fileName, uploadedAt, possibleAnswers
            };

            const submitResult = await this.promotionQuestionsState.submitQuestion(payload);

            if (submitResult) {
                this.promotionQuestionsState.setCurrentQuestion(null);
                this.promotionQuestionsState.setRecentlyUpdatedQuestion(submitResult);
                await this.promotionQuestionsState.refreshQuestions();
                this.questionModelV2.reset();
                this.formV2.reset();
            } else {
                this.errorMessage = 'Unknown error. Something went wrong.';
            }
        }
        this.isLoading = false;
    };

    private preventDefaults = (event: React.SyntheticEvent) => {
        event.preventDefault();
        event.stopPropagation();
    };
}
